/**
 * @file useRegister.js
 * @description 注册弹窗逻辑（手机号 + 滑块 + 短信验证码）
 * @module features/user/composables/useRegister
 */

import { computed, nextTick, ref, watch } from 'vue'
import {
  isAuthVerifyFieldError,
  resolveAuthVerifyErrorMessage,
} from '@/shared/utils/apiErrorCodes'
import { useUserStore } from '@/stores/user'
import { sendSmsApi, signUpApi } from '@/features/user/api/userApi'
import { buildStoredSession, saveAuthUser } from '@/shared/utils/auth'

const SMS_COOLDOWN_SECONDS = 60

/**
 * @param {{ showRegister: boolean }} props
 * @param {(event: 'close' | 'show-login' | 'register-success', payload?: unknown) => void} emit
 */
export function useRegister(props, emit) {
  const mobile = ref('')
  const verifyCode = ref('')
  const mobileError = ref('')
  const verifyCodeError = ref('')
  const verifyCodeFieldError = ref('')
  const apiError = ref('')
  const sendCodeCountdown = ref(0)
  const isVerified = ref(false)

  const sliderVerifyRef = ref(null)

  const isFormInvalid = computed(
    () =>
      mobileError.value ||
      verifyCodeError.value ||
      !mobile.value.trim() ||
      !verifyCode.value.trim() ||
      !/^1\d{10}$/.test(mobile.value.trim()) ||
      !/^\d{6}$/.test(verifyCode.value.trim())
  )

  const sendCodeDisabled = computed(
    () =>
      sendCodeCountdown.value > 0 ||
      !/^1\d{10}$/.test(mobile.value.trim()) ||
      !isVerified.value
  )

  const sendCodeBtnText = computed(() => {
    if (sendCodeCountdown.value > 0) {
      return `${sendCodeCountdown.value}秒后重发`
    }
    return '获取验证码'
  })

  const verifyCodeInputClass = computed(() => {
    const hasClient = !!verifyCodeError.value
    const hasApi = !!verifyCodeFieldError.value
    return {
      'input-error': hasClient || hasApi,
      'input-animated': hasClient,
      'auth-verify-api-error': hasApi,
    }
  })

  function applyVerifyOrBannerError(payload, fallbackBannerMsg) {
    const code = payload?.code
    const msg = payload?.msg || payload?.errmsg
    if (isAuthVerifyFieldError(code)) {
      verifyCodeFieldError.value = resolveAuthVerifyErrorMessage(code, msg)
      apiError.value = ''
      return
    }
    verifyCodeFieldError.value = ''
    apiError.value = msg || fallbackBannerMsg
  }

  function startSendCodeCountdown() {
    sendCodeCountdown.value = SMS_COOLDOWN_SECONDS
    const timer = setInterval(() => {
      sendCodeCountdown.value--
      if (sendCodeCountdown.value <= 0) clearInterval(timer)
    }, 1000)
  }

  function validateMobile() {
    if (!mobile.value.trim()) {
      mobileError.value = '手机号不能为空'
      return
    }
    if (!/^1\d{10}$/.test(mobile.value.trim())) {
      mobileError.value = '请输入正确的手机号'
    } else {
      mobileError.value = ''
    }
  }

  function validateVerifyCode() {
    if (!verifyCode.value.trim()) {
      verifyCodeError.value = '验证码不能为空'
      return
    }
    if (!/^\d{6}$/.test(verifyCode.value.trim())) {
      verifyCodeError.value = '验证码为6位数字'
    } else {
      verifyCodeError.value = ''
    }
  }

  async function sendVerifyCode() {
    if (!isVerified.value) return
    validateMobile()
    if (mobileError.value) return
    const mobileValue = mobile.value.trim()
    try {
      const response = await sendSmsApi({ mobile: mobileValue })
      if (response.data.code === 200 || response.data.code === 0) {
        verifyCodeFieldError.value = ''
        startSendCodeCountdown()
      } else {
        applyVerifyOrBannerError(response.data, '发送失败')
      }
    } catch (error) {
      const data = error.response?.data
      if (data) {
        applyVerifyOrBannerError(data, '网络请求失败，请稍后再试')
      } else {
        verifyCodeFieldError.value = ''
        apiError.value = '网络请求失败，请稍后再试'
      }
    }
  }

  async function submitForm() {
    validateMobile()
    validateVerifyCode()
    if (mobileError.value || verifyCodeError.value || isFormInvalid.value) {
      apiError.value = '请填写正确的手机号和验证码'
      return
    }
    try {
      apiError.value = ''
      verifyCodeFieldError.value = ''
      const response = await signUpApi({
        mobile: mobile.value.trim(),
        verifyCode: verifyCode.value.trim(),
      })
      if (response.data.code === 200 || response.data.code === 0) {
        const user = response.data.data
        const userStore = useUserStore()
        const tokenData = buildStoredSession(user)
        userStore.setUser(tokenData)
        saveAuthUser(tokenData)
        if (sliderVerifyRef.value) {
          sliderVerifyRef.value.reset()
          isVerified.value = false
        }
        emit('register-success', tokenData)
      } else {
        applyVerifyOrBannerError(response.data, '注册失败')
      }
    } catch (error) {
      const data = error.response?.data
      if (data) {
        applyVerifyOrBannerError(data, '网络请求失败，请稍后再试')
      } else {
        verifyCodeFieldError.value = ''
        apiError.value = '网络请求失败，请稍后再试'
      }
    }
  }

  function handleVerifySuccess() {
    isVerified.value = true
  }

  function resetSliderVerify() {
    if (sliderVerifyRef.value) {
      sliderVerifyRef.value.reset()
      isVerified.value = false
    }
  }

  watch(verifyCode, () => {
    verifyCodeFieldError.value = ''
  })

  watch(
    () => props.showRegister,
    (newVal) => {
      if (!newVal) {
        sendCodeCountdown.value = 0
        verifyCodeFieldError.value = ''
        resetSliderVerify()
      }
    }
  )

  watch(mobile, () => {
    verifyCodeFieldError.value = ''
    if (!isVerified.value) return
    nextTick(() => {
      resetSliderVerify()
    })
  })

  return {
    mobile,
    verifyCode,
    mobileError,
    verifyCodeError,
    verifyCodeFieldError,
    apiError,
    sendCodeCountdown,
    isVerified,
    sliderVerifyRef,
    isFormInvalid,
    sendCodeDisabled,
    sendCodeBtnText,
    verifyCodeInputClass,
    validateMobile,
    validateVerifyCode,
    sendVerifyCode,
    submitForm,
    handleVerifySuccess,
  }
}
